"use client";

import { useEffect, useMemo } from "react";
import { useSnapshot } from "valtio/react";
import { rootStore } from "@store/rootStore";
import { getOrCreateClientUuid, getTracker } from "@utils/openreplay";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { Track, type TrackProfile } from "@/src/lib/tracking";

/**
 * OpenReplay 身份接线（开发步骤 Step 3）。
 *
 * 规范：`docs/engineering/tracking/event-data-dictionary.md` §3.1 / §3.2。
 * - 登录：`setUserID(ownerId)` + 写全部身份 metadata（明文，2026-08-07 决策）
 * - 匿名：`setUserID(clientUuid)`，保证登录前后同一条 session 连得上
 * - **登出：必须 `Track.clearIdentity()`** —— metadata 没有 delete，只能整体覆盖成空串
 *
 * ## 为什么登出清理放在这里，而不是各个 logout 调用点
 *
 * 登出有多条触发路径（用户主动登出、token 过期、刷新失败）。本 hook 监听的是
 * `isLoggedIn` **状态**，无论哪条路径导致状态翻转都会被覆盖到 —— 比在每个调用点各加一行更难漏。
 */

/** tracker 是懒加载的，身份要等它就绪；20 × 250ms 约 5s 内重试。 */
function retryUntilTrackerReady(fn: () => void) {
  let attempts = 0;
  const maxAttempts = 20;
  const tick = () => {
    attempts += 1;
    if (getTracker()) {
      fn();
      return;
    }
    if (attempts < maxAttempts) {
      setTimeout(tick, 250);
    }
  };
  tick();
}

/** 用户自己填的 Twitter handle，进入任务页后写入（与 `global.js` 的 `getTwitterName` 同源）。 */
function readTwitterHandle(): string | null {
  try {
    return window.localStorage.getItem("_twitter_name");
  } catch {
    return null;
  }
}

export function useOpenReplayIdentity() {
  const { isLoggedIn, isLoading, isFetching } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { data: member } = useMemberUser();

  const plan = useMemo(() => {
    if (member?.isExpert) {
      return "export" as const;
    }
    if (member?.isPro) {
      return "pro" as const;
    }
    return "free" as const;
  }, [member?.isExpert, member?.isPro]);

  const ownerId = ownerInfo?.id == null ? null : String(ownerInfo.id);
  const email = (ownerInfo as { issue_email?: string } | null)?.issue_email;
  const loginChannel = (ownerInfo as { issue_platform?: string } | null)
    ?.issue_platform;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    // 登录态还没定下来时不动身份 —— 否则会在 Loading 期间先清一次，造成短暂的空窗
    if (isLoading || isFetching) {
      return;
    }

    retryUntilTrackerReady(() => {
      if (!isLoggedIn || !ownerId) {
        // 匿名期：保持 session 连续性用 clientUuid，但身份字段必须是空的
        Track.identify(getOrCreateClientUuid());
        Track.clearIdentity();
        Track.context({ plan });
        return;
      }

      const profile: TrackProfile = {
        ownerId,
        email,
        twitterHandle: readTwitterHandle(),
        loginChannel,
      };
      Track.identify(ownerId, profile);
      Track.context({ plan });
    });
  }, [isLoggedIn, ownerId, email, loginChannel, isLoading, isFetching, plan]);
}
